import * as stylex from "@stylexjs/stylex";
import useLocale from "hooks/useLocale";
import React from "react";
import { useInView } from "react-intersection-observer";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { FigureStateProvider, foldFigureControl, useFigureState, useUrlFigureState } from "../domain/figure-state";
import { useMedia } from "../hooks";
import { breakpoints, colors, layoutAreas } from "../materials/consts.stylex";
import { Grid } from "../materials/grid";
import { typography } from "../materials/typography.stylex";
import { O, pipe, R } from "../prelude";
import { withDataLoader } from "./data-loader";
import { FigureControls } from "./figure-controls";
import { MultiLogoIO } from "./logo";
import MobileControlPanel from "./mobile-controls-panel";
import { DownloadButton, InfoButton, ShareButton } from "./tooltip-triggers";
const styles = {
    root: {
        kLKAdn: "x6gltce",
        kGO01o: "x163t5y5",
        $$css: true
    },
    figurebodyBase: {
        ksoyAI: "xzt5al7",
        keoZOQ: "xdj266r",
        $$css: true
    },
    figurebodyNarrow: {
        kBCFzs: "x1uq3dmu",
        $$css: true
    },
    figurebodyMain: {
        kBCFzs: "x1uq3dmu",
        $$css: true
    },
    figurebodyFigure: {
        kBCFzs: "xvaai34",
        keoZOQ: "xdj266r",
        $$css: true
    },
    figurebodyWide: {
        kBCFzs: "xki0xp4",
        $$css: true
    },
    title: {
        k1K539: "x1yztbdb",
        kgeoSG: "x1oemppz",
        kP1sEF: "x1fgarty",
        k7yrt4: "xuwqecn",
        $$css: true
    }
};
export const Divider = ()=>{
    return <div className="x10w6t97 x1i1rx1s x1o6q14k xq1n1xh x15uhsjy xg01cxk x4gxml0 x1s85apg xbh2guc"/>;
};
const DividerSilent = ()=>{
    return <div role="presentation" className="x1kky2od"/>;
};
export const ControlBox = ({ children, orientation = "horizontal" })=>{
    const items = React.Children.toArray(children).filter((c)=>c !== null);
    const itemClassName = {
        0: {
            className: "x98rzlu x193iq5w x1yf7rl7 xeu0qjq"
        },
        1: {
            className: "x98rzlu xgqtt45 x193iq5w"
        }
    }[!!(orientation === "vertical") << 0].className;
    return <div {...{
        0: {
            className: "x78zum5 x1a02dak"
        },
        1: {
            className: "x78zum5 x1a02dak xh8yej3 xdt5ytf"
        }
    }[!!(orientation === "vertical") << 0]}>
      {items.map((c, i)=>{
        return <div // `.figure-control-box-item--only-child [data-control-type="select"]` in
        // style.global.css — the Select this may wrap sets that attribute itself, an
        // element we don't render, so StyleX can't reach it directly.
        className={items.length === 1 ? `figure-control-box-item--only-child ${itemClassName}` : itemClassName} key={i}>
            {c}
          </div>;
    })}
    </div>;
};
const sourceMessageDescriptor = defineMessage({
    id: "Source: {source}.",
    defaultMessage: "Source: {source}."
});
const Figure = ({ children, id, csv, xlsx, title, caption, source: sourceRaw, renderMode, size = "figure", layout = "article", disableEmbed, dataset })=>{
    const intl = useIntl();
    const screenMDown = !useMedia(breakpoints.m);
    const parsedFigureState = useUrlFigureState();
    const [ref, inView] = useInView({
        rootMargin: "25%",
        triggerOnce: true
    });
    // Assignment with detours to improve variable naming in translated string
    const source = sourceRaw ? intl.formatMessage(sourceRaw) : undefined;
    const sourceText = source ? intl.formatMessage(sourceMessageDescriptor, {
        source
    }) : undefined;
    const captionText = intl.formatMessage(caption);
    const titleText = title ? intl.formatMessage(title) : parsedFigureState.title;
    const controlValues = pipe(parsedFigureState.controls, O.map((s)=>R.toArray(s).map(([_, c])=>foldFigureControl(c, {
                ButtonGroup: (control)=>control.selected,
                Checkbox: (control)=>control.selected.toString() === "true" ? control.label : undefined,
                SingleSelect: (control)=>O.toUndefined(control.selected),
                MultiSelect: (control)=>`${pipe(control.selected, O.map((d)=>`${control.label}: ${d.join(", ")}`), O.toUndefined)}`,
                Timeline: (control)=>control.selected.toString(),
                Radio: (control)=>O.toUndefined(control.selected),
                MultiCheckbox: (control)=>`${pipe(control.selected, O.map((d)=>`${control.label}: ${d.join(", ")}`), O.toUndefined)}`
            }))), O.toUndefined);
    const controlValuesText = controlValues ? <FormattedMessage id="Selection: {0}." defaultMessage="Selection: {0}." values={{
        "0": controlValues.filter(Boolean).join("; ")
    }}/> : undefined;
    const iconControls = <div className="x78zum5 x1q0g3np x1yztbdb">
      {renderMode !== "embed" && <>
          <InfoButton content={captionText} source={sourceText}/>
          <DividerSilent/>
        </>}
      <DownloadButton id={id} csv={csv} xlsx={xlsx}/>
      <DividerSilent/>
      <ShareButton id={id} message={titleText} disableEmbed={disableEmbed} dataset={dataset}/>
    </div>;
    return <FigureStateProvider initialState={parsedFigureState}>
      {renderMode === "static" ? <StaticChart layout={size === "narrow" ? "rows" : "columns"} title={titleText} source={sourceText} caption={captionText} controlValues={controlValuesText}>
          {children}
        </StaticChart> : renderMode === "embed" ? <EmbedChart caption={captionText} source={sourceText} iconControls={iconControls}>
          {children}
        </EmbedChart> : <Grid layout={layout} as="figure" sx={[
        styles.root
    ]}>
          <div className="x193iq5w x1uq3dmu" ref={ref} id={id}>
            {title && <h3 {...stylex.props(typography.heading4, styles.title)}>{titleText}</h3>}
            <div {...{
        0: {
            className: "x78zum5 xdt5ytf xfiabkx"
        },
        1: {
            className: "x78zum5 xdt5ytf xfiabkx x1yztbdb"
        }
    }[!!!screenMDown << 0]}>
              {!screenMDown && iconControls}

              {!screenMDown && <>
                  <FigureControls position="TOP"/>
                  <FigureControls showDivider={false} position="TOOLBAR"/>
                </>}
            </div>
          </div>

          {inView && <FigureView size={size}>{children}</FigureView>}

          {screenMDown ? <div className="x78zum5 x6s0dn4">
              {iconControls}
              <div className="x1iyjqo2"></div>
              <FigureControls position="TOOLBAR"/>
            </div> : null}
        </Grid>}
    </FigureStateProvider>;
};
const FigureView = ({ children, size = "figure" })=>{
    const [state] = useFigureState();
    const screenMDown = !useMedia(breakpoints.m);
    const hasSideControls = pipe(state.controls, O.map((controls)=>{
        return R.toArray(controls).reduce((prev, [, control])=>control.position === "LEFT" || control.position === "RIGHT" || prev, true);
    }), O.toNullable);
    const figurebodyClassName = stylex.props(styles.figurebodyBase, figurebodyStyleForSize(size)).className;
    const figureBodyProps = <div // `.figurebody-narrow > *` in style.global.css — max-width applies to `children`,
    // arbitrary chart content passed in, not something StyleX can target directly.
    className={size === "narrow" ? `figurebody-narrow ${figurebodyClassName}` : figurebodyClassName}>
      {children}
    </div>;
    return <>
      {screenMDown && hasSideControls ? <>
          <MobileControlPanel/>
          {figureBodyProps}
        </> : <>
          <SideControls side="left" visible={hasSideControls}>
            <FigureControls showDivider={false} position="LEFT"/>
          </SideControls>
          {figureBodyProps}
          <SideControls side="right" visible={hasSideControls}>
            <FigureControls showDivider={false} position="RIGHT"/>
          </SideControls>
        </>}
    </>;
};
function figurebodyStyleForSize(size) {
    switch(size){
        case "narrow":
            return styles.figurebodyNarrow;
        case "main":
            return styles.figurebodyMain;
        case "figure":
            return styles.figurebodyFigure;
        case "wide":
            return styles.figurebodyWide;
    }
}
const SideControls = ({ side, visible, children })=>{
    return <div {...{
        0: {
            className: "x1uq3dmu x15nvg45"
        },
        2: {
            className: "x1uq3dmu xma2rwv"
        },
        1: {
            className: "x1uq3dmu x15nvg45 x1s85apg"
        },
        3: {
            className: "x1uq3dmu xma2rwv x1s85apg"
        }
    }[!!(side === "left") << 1 | !!!visible << 0]}>
      {children}
    </div>;
};
const embedChartStyles = {
    meta: {
        k1xSpc: "x78zum5",
        kjj79g: "x1qughib",
        kmVPX3: "x1g8o3q3",
        kWkggS: "x12peec7",
        kXwgrk: "xdt5ytf xpohigx",
        kGNEyG: "x1cy8zhl x6fk5fs",
        kOIVth: "xou54vl xwapvzr",
        $$css: true
    },
    metaHero: {
        km5ZXQ: "x1o6q14k",
        $$css: true
    },
    small: {
        k1xSpc: "x1lliihq",
        $$css: true
    },
    link: {
        k1xSpc: "x1lliihq",
        keoZOQ: "x1gslohp",
        k63SB2: "xk50ysn",
        kMwMTN: "x1hcezkj",
        k67Jb4: "xt0b8zv",
        $$css: true
    }
};
export const EmbedChart = ({ children, caption, source, iconControls, isHeroChart })=>{
    const locale = useLocale();
    const screenMDown = !useMedia(breakpoints.m);
    const logoEl = <MultiLogoIO size="small" variant="positive"/>;
    return <div className="x1ikqzku x19bbpc0 x1v4z155 x1n2onr6 x196nufh">
      <div className="x78zum5 xdt5ytf xfiabkx">
        {!screenMDown && iconControls}
        <FigureControls/>
      </div>

      <div className="x1n2onr6 x78zum5 xdt5ytf x1qughib xw7yly9">
        <div {...{
        0: {
            className: "xgc0pbm xh8yej3 x98rzlu"
        },
        1: {
            className: "xgc0pbm xh8yej3 x98rzlu xzt5al7"
        }
    }[!!// Hero charts work with RTL, others seem to break
    !isHeroChart << 0]}>
          {children}
        </div>
        <div {...stylex.props(typography.table, embedChartStyles.meta, isHeroChart && embedChartStyles.metaHero)}>
          <div>
            <div {...stylex.props(typography.meta, embedChartStyles.small)}>{caption}</div>
            <div {...stylex.props(typography.meta, embedChartStyles.small)}>{source}</div>
            <a href={`https://www.education-progress.org/${locale}`} target="_blank" rel="noreferrer" {...stylex.props(typography.body2, embedChartStyles.link)}>
              <FormattedMessage id="Visit the GEM Report SCOPE website" defaultMessage="Visit the GEM Report SCOPE website"/>
            </a>
          </div>
          {logoEl}
        </div>
      </div>
    </div>;
};
const staticChartStyles = {
    meta: {
        k1xSpc: "x78zum5",
        kWkggS: "x12peec7",
        kjj79g: "x1qughib",
        kGNEyG: "x6s0dn4",
        kmVPX3: "x1g8o3q3",
        $$css: true
    },
    metaAside: {
        kXwgrk: "xdt5ytf",
        ks0D6T: "x1891jpw",
        kmVPX3: "x1717udv",
        kLKAdn: "xyamay9",
        kE3dHu: "x1tudf5h",
        $$css: true
    },
    title: {
        kMwMTN: "xjvpvmj",
        $$css: true
    },
    small: {
        k1xSpc: "x1lliihq",
        $$css: true
    },
    smallPadded: {
        kmVPX3: "x1ib1h6n",
        $$css: true
    }
};
export const StaticChart = ({ children, layout, title, source, caption, controlValues })=>{
    const locale = useLocale();
    const today = new Date();
    const options = {
        year: "numeric",
        month: "short",
        day: "numeric"
    };
    const formatted = today.toLocaleDateString(locale, options);
    const isAside = layout === "rows";
    const logoEl = <MultiLogoIO size="small" variant="positive"/>;
    const header = <div>
      {title && <h3 {...stylex.props(typography.heading4, staticChartStyles.title)}>{title}</h3>}
      {caption}
    </div>;
    return <div className="x1ikqzku x19bbpc0 x1v4z155 x1n2onr6 x196nufh">
      {!isAside && header}

      <div {...{
        0: {
            className: "x1n2onr6 x78zum5 x1qughib xw7yly9 xdt5ytf"
        },
        1: {
            className: "x1n2onr6 x78zum5 x1qughib xw7yly9 x1nuk7h8 x1q0g3np"
        }
    }[!!isAside << 0]}>
        <div className="xzt5al7 x2xcw19 xgc0pbm xh8yej3 x98rzlu">{children}</div>
        <div {...stylex.props(typography.table, staticChartStyles.meta, isAside && staticChartStyles.metaAside)}>
          {isAside && header}
          <div>
            <div {...stylex.props(typography.meta, staticChartStyles.small)}>
              <FormattedMessage id="This graphic was designed for the GEM Report Education Progress website." defaultMessage="This graphic was designed for the GEM Report Education Progress website."/>
              <a href="https://www.education-progress.org"> www.education-progress.org</a>{" "}
            </div>
            <div {...stylex.props(typography.meta, staticChartStyles.small, isAside && staticChartStyles.smallPadded)}>
              {controlValues} {controlValues && <br/>}
              {source} {isAside && <br/>}
              <FormattedMessage id="Accessed" defaultMessage="Accessed"/>: {formatted}
            </div>
            {isAside && logoEl}
          </div>
          {!isAside && logoEl}
        </div>
      </div>
    </div>;
};
export function withFigureIO(props) {
    const { url, csv, xlsx, metadata, Data, Chart, size = "figure", height = {
        narrow: 500,
        wide: 500
    }, datasets } = props;
    return ({ renderMode, dataset, renderMultiple = false })=>{
        const Renderer = dataset && datasets ? withDataLoader(datasets[dataset].url, Data, Chart, height) : withDataLoader(url, Data, Chart, height);
        return <>
        <Figure {...metadata} size={size} renderMode={renderMode} csv={csv} xlsx={xlsx} {...datasets && dataset && {
            dataset
        }}>
          <Renderer renderMode={renderMode}/>
        </Figure>
        {renderMultiple && Object.entries(datasets).map(([key, value])=>{
            const Renderer = withDataLoader(value.url, Data, Chart, height);
            return <>
                <h2 className="x2b8uid">Dataset: {value.filename}</h2>
                <Figure key={key} {...metadata} size={size} renderMode="embed" csv={value.csv} xlsx={value.xlsx} dataset={key}>
                  <Renderer renderMode="embed"/>
                </Figure>
              </>;
        })}
      </>;
    };
}
export function withFigureIOExplorer({ metadata, Chart, size = "main", csv, xlsx }) {
    return ({ renderMode })=><>
      <Figure {...metadata} size={size} layout={"explorer"} renderMode={renderMode} /**
         * @FIXME
         * needs discussion, static data does not necessarily make sense for simulations
         */ csv={csv} xlsx={xlsx} // FIXME: ideally we should be able to embed the explorer, but it needs some work
        disableEmbed={true}>
        <Chart renderMode={renderMode}/>
      </Figure>
      <div data-loader-ready className="x1s85apg"/>
    </>;
}
